import { ReactNode, useMemo } from 'react';
import {
  EthereumClient,
  w3mConnectors,
  w3mProvider,
} from '@web3modal/ethereum';
import {
  Chain,
  ChainProviderFn,
  configureChains,
  createConfig,
  WagmiConfig as WagmiConfigProvider,
} from 'wagmi';
import { goerli, mainnet } from 'wagmi/chains';

import createSafeContext from '#/utils/createSafeContext';

import { useSystemConfig } from '#/features/system/system-config-context';

/**
 * @see https://walletconnect.com/explorer
 */
const RECOMMENDED_WALLET_IDS = [
  'c57ca95b47569778a828d19178114f4db188b89b763c899ba0be274e97267d96', // MetaMask
  '5864e2ced7c293ed18ac35e0db085c09ed567d67346ccb6f58a0327a75137489', // Fireblocks
];

interface WalletConnectConfig {
  readonly projectId: string;
  readonly ethereumClient: EthereumClient;
  readonly recommendedWalletIds: string[];
  readonly chain: Chain;
}

export const [useWalletConnectConfig, ConfigProvider] =
  createSafeContext<WalletConnectConfig>('WalletConnectConfig');

interface Props {
  readonly children: ReactNode;
}

export function WalletConnectConfigProvider(props: Props) {
  const config = useSystemConfig();
  const ethereumChainId = config.ethereum.chainId;

  const walletConnectConfig = useMemo(() => {
    const projectId = WALLET_CONNECT_PROJECT_ID;
    const recommendedWalletIds = RECOMMENDED_WALLET_IDS;
    const isMainnet = ethereumChainId === mainnet.id;
    const chain: Chain = isMainnet ? mainnet : goerli;
    /**
     * `[chain, chain]` configuration is temporarily needed to avoid QR code and mobile browser connection errors
     *   the solution recommended by WalletConnect developers @see https://github.com/orgs/WalletConnect/discussions/3595#discussioncomment-6914595
     */
    const availableChains: [Chain, Chain] = [chain, chain];
    const chainProviders = [
      // WIP: latest `wagmi` version has issues with `w3mProvider` return type `any`; Try removing assertions in the future
      w3mProvider({ projectId }) as ChainProviderFn<Chain>,
    ];

    const { publicClient } = configureChains(availableChains, chainProviders);
    const wagmiConfig = createConfig({
      autoConnect: true,
      connectors: w3mConnectors({ projectId, chains: availableChains }),
      publicClient,
    });
    const ethereumClient = new EthereumClient(wagmiConfig, availableChains);

    return {
      projectId,
      chain,
      ethereumClient,
      recommendedWalletIds,
      wagmiConfig,
    };
  }, [ethereumChainId]);

  return (
    <WagmiConfigProvider config={walletConnectConfig.wagmiConfig}>
      <ConfigProvider value={walletConnectConfig}>
        {props.children}
      </ConfigProvider>
    </WagmiConfigProvider>
  );
}
